<script lang="ts">
    import { getTypedContext } from "@lib/svelte-typed-context";

    import { tabsNameContextToken } from "./constants";
    import TabsManager from "./TabsManager";
    import type { TabItem } from "./types";

    export let tabsManager: TabsManager;
    export let tabs: TabItem[];
    export let position: "start" | "center" | "end" = "center";

    let tabRefs: HTMLButtonElement[] = [];

    const tabsName = getTypedContext(tabsNameContextToken);
    const openedTab$ = tabsManager.openedTab$;
    const handleOpenTab = (name: string) => {
        tabsManager.openTab(name);
    };
    const keyDown = (key: string, index: number, e: KeyboardEvent) => {
        switch (key) {
            case "ArrowLeft":
                tabRefs[(index - 1 + tabRefs.length) % tabRefs.length].focus();
                e.stopPropagation();
                return;
            case "ArrowRight":
                tabRefs[(index + 1) % tabRefs.length].focus();
                e.stopPropagation();
                return;
        }
    };
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
<div class="gw-tabs-menu">
    <div class="gw-tabs-menu__content">
        <div
            class="gw-tabs-menu__list"
            role="tablist"
            style={`--gw-tabs-menu-position: ${position}`}
            aria-label={tabsName}
        >
            {#each tabs as tab, index}
                <button
                    role="tab"
                    id={`gw-tabcontrol-${tabsName}-${tab.name}`}
                    class="gw-tabs-menu__item gw-tabs-menu__btn"
                    class:gw-tabs-menu__btn--opened={tab.name === $openedTab$}
                    aria-controls={`gw-tab-${tabsName}-${tab.name}`}
                    aria-selected={tab.name === $openedTab$}
                    on:click|preventDefault={() => handleOpenTab(tab.name)}
                    on:keydown={(e) => keyDown(e.key, index, e)}
                    bind:this={tabRefs[index]}
                >
                    {tab.label}
                    {#if tab.tag}
                        <span class="gw-tabs-menu__count">
                            ({tab.tag})
                        </span>
                    {/if}
                </button>
            {/each}
        </div>

        <div class="gw-tabs__hr" />
    </div>

    <div class="qw-tabs-menu__right">
        <slot />
    </div>
</div>

<style lang="postcss">
    .gw-tabs-menu {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.75em;
    }

    .gw-tabs-menu__content {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .gw-tabs__hr {
        width: 100%;
        background: var(--grey-300, #e4e7ec);
        height: 0.125em;
        margin-top: -0.125em;
    }

    .qw-tabs-menu__right {
        margin-top: 1.65em;
    }

    .gw-tabs-menu__list {
        display: flex;
        justify-content: var(--gw-tabs-menu-position);
        flex-wrap: wrap;
        gap: 0.4em;

        &:focus-visible,
        & :focus-visible {
            border-radius: var(--gw-border-radius-md);
            outline: 3px solid var(--gw-tabs-tab-btn);
            outline-offset: 2px;
        }
    }

    .gw-tabs-menu__btn {
        display: block;
        padding: 0.5em 0;
        transition: background-color var(--gw-default-transition),
            border-bottom var(--gw-default-transition);
        background-color: var(--gw-tabs-no-selected-color);
        border: 0;
        color: #1d2939;
        text-transform: capitalize;
        font-size: 16px;
        line-height: 20px;
        font-weight: var(--gw-font-weight-semibold);
        opacity: 0.7;
        cursor: pointer;

        &:hover,
        &:focus {
            /* opacity: 0.8; */

            background-image: linear-gradient(
                to bottom,
                var(--gw-tabs-underline-color) 0,
                var(--gw-tabs-underline-color) 100%
            );
            background-position: 0 100%;
            background-repeat: repeat-x;
            background-size: 1px 2px;
        }
    }
    .gw-tabs-menu__btn--opened {
        background-image: linear-gradient(
            to bottom,
            var(--gw-tabs-underline-color) 0,
            var(--gw-tabs-underline-color) 100%
        );
        background-position: 0 100%;
        background-repeat: repeat-x;
        background-size: 1px 2px;
        color: var(--gw-tabs-tab-btn);
        opacity: 1;
        position: relative;
        z-index: 1;
    }

    .gw-tabs-menu__count {
        line-height: 1em;
    }
</style>
